import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import {
  clampCmykChannel,
  cmykToHex,
  hexToCmyk,
  normalizePrintHex,
} from "./print-color.util";
import type { PrintCmyk } from "./print-color.interface";

const INPUT_DEBOUNCE_MS = 280;

@Component({
  selector: "app-print-color",
  templateUrl: "./print-color.component.html",
  styleUrls: ["./print-color.component.scss"],
  standalone: false,
})
export class PrintColorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label = "";
  @Input() value = "#000000";
  @Input() showCmyk = true;
  @Output() valueChange = new EventEmitter<string>();

  hexValue = "#000000";
  cmyk: PrintCmyk = { c: 0, m: 0, y: 0, k: 100 };

  private readonly hexInput$ = new Subject<string>();
  private readonly cmykInput$ = new Subject<{
    channel: keyof PrintCmyk;
    raw: string;
  }>();
  private readonly sub = new Subscription();

  ngOnInit(): void {
    this.syncFromValue(this.value);
    this.sub.add(
      this.hexInput$
        .pipe(debounceTime(INPUT_DEBOUNCE_MS))
        .subscribe((value) => this.commitHex(value)),
    );
    this.sub.add(
      this.cmykInput$
        .pipe(debounceTime(INPUT_DEBOUNCE_MS))
        .subscribe(({ channel, raw }) => this.commitCmyk(channel, raw)),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["value"] && !changes["value"].firstChange) {
      this.syncFromValue(this.value);
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onPickerInput(value: string): void {
    this.emitHex(value);
  }

  onHexInput(value: string): void {
    this.hexValue = value;
    this.hexInput$.next(value);
  }

  onHexBlur(value: string): void {
    this.commitHex(value);
  }

  onCmykInput(channel: keyof PrintCmyk, raw: string): void {
    const parsed = Number.parseFloat(raw);
    if (!Number.isNaN(parsed)) {
      this.cmyk = {
        ...this.cmyk,
        [channel]: clampCmykChannel(parsed),
      };
    }
    this.cmykInput$.next({ channel, raw });
  }

  onCmykBlur(channel: keyof PrintCmyk, raw: string): void {
    this.commitCmyk(channel, raw);
  }

  private commitHex(value: string): void {
    const normalized = normalizePrintHex(value);
    if (!normalized) {
      this.syncFromValue(this.value);
      return;
    }
    this.emitHex(normalized);
  }

  private commitCmyk(channel: keyof PrintCmyk, raw: string): void {
    const parsed = Number.parseFloat(raw);
    const next = {
      ...this.cmyk,
      [channel]: clampCmykChannel(parsed),
    };
    this.cmyk = next;
    this.emitHex(cmykToHex(next));
  }

  private emitHex(value: string): void {
    const normalized = normalizePrintHex(value);
    if (!normalized || normalized === this.value) {
      this.syncFromValue(normalized ?? this.value);
      return;
    }
    this.syncFromValue(normalized);
    this.valueChange.emit(normalized);
  }

  private syncFromValue(value: string): void {
    const normalized = normalizePrintHex(value) ?? "#000000";
    this.hexValue = normalized;
    this.cmyk = hexToCmyk(normalized);
  }
}
